<script setup>
import { useSlots } from "vue";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";

const slots = useSlots();

const props = defineProps({
  icon: { type: String, default: null },
  skin: { type: String, default: null } // border, shadow, gray, dark
});
</script>

<template>
  <button
    type="button"
    class="image-review-button"
    :class="{
      [`image-review-button--${props.skin}`]: true,
      'image-review-button--no-slot': !slots.default
    }"
  >
    <svg
      v-if="props.skin === 'shadow'"
      class="image-review-button__shadow"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 210 87"
      preserveAspectRatio="none"
    >
      <g filter="url(#a)">
        <ellipse cx="79.162" cy="51.962" fill="#FF8B37" rx="46.313" ry="8.038" />
      </g>
      <g filter="url(#b)">
        <ellipse
          cx="45.825"
          cy="8.038"
          fill="#FF8B37"
          rx="45.825"
          ry="8.038"
          transform="matrix(-1 0 0 1 181.05 27)"
        />
      </g>
      <g filter="url(#c)">
        <ellipse cx="135.225" cy="51.962" fill="#2A66FF" rx="45.825" ry="8.038" />
      </g>
      <g filter="url(#d)">
        <ellipse
          cx="46.313"
          cy="8.038"
          fill="#2A66FF"
          rx="46.313"
          ry="8.038"
          transform="matrix(-1 0 0 1 122.55 27)"
        />
      </g>
      <g filter="url(#e)">
        <ellipse
          cx="159.113"
          cy="43.5"
          fill="#BB2AFF"
          rx="13.961"
          ry="23.887"
          transform="rotate(90 159.113 43.5)"
        />
      </g>
      <g filter="url(#f)">
        <ellipse
          cx="50.888"
          cy="43.5"
          fill="#BB2AFF"
          rx="13.961"
          ry="23.887"
          transform="rotate(90 50.888 43.5)"
        />
      </g>
      <defs>
        <filter
          id="a"
          width="145.225"
          height="68.677"
          x="6.55"
          y="17.623"
          color-interpolation-filters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur result="effect1_foregroundBlur_3196_62568" stdDeviation="13.15" />
        </filter>
        <filter
          id="b"
          width="144.25"
          height="68.677"
          x="63.099"
          y=".7"
          color-interpolation-filters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur result="effect1_foregroundBlur_3196_62568" stdDeviation="13.15" />
        </filter>
        <filter
          id="c"
          width="144.25"
          height="68.677"
          x="63.1"
          y="17.623"
          color-interpolation-filters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur result="effect1_foregroundBlur_3196_62568" stdDeviation="13.15" />
        </filter>
        <filter
          id="d"
          width="145.225"
          height="68.677"
          x="3.625"
          y=".7"
          color-interpolation-filters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur result="effect1_foregroundBlur_3196_62568" stdDeviation="13.15" />
        </filter>
        <filter
          id="e"
          width="100.375"
          height="80.523"
          x="108.925"
          y="3.238"
          color-interpolation-filters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur result="effect1_foregroundBlur_3196_62568" stdDeviation="13.15" />
        </filter>
        <filter
          id="f"
          width="100.375"
          height="80.523"
          x=".7"
          y="3.238"
          color-interpolation-filters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur result="effect1_foregroundBlur_3196_62568" stdDeviation="13.15" />
        </filter>
      </defs>
    </svg>

    <span class="image-review-button__bg">
      <span
        v-if="icon"
        class="image-review-button__icon-wrapper"
        :class="{
          'image-review-button__icon-wrapper--rotate': icon === 'loader'
        }"
      >
        <UseSvg :icon="icon" />
      </span>

      <slot></slot>
    </span>
  </button>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";
@import "frontend/init/jiffy/button/mixin";

.image-review-button {
  $this: &;

  @include clear-button;

  font-size: 15px;
  font-weight: 600;
  width: 100%;
  height: 40px;

  &--shadow {
    position: relative;
    z-index: 0;

    #{$this}__bg {
      background-color: $white;
      color: $pod-gray-modern-900;
    }
  }

  &--gray {
    #{$this}__bg {
      background-color: $pod-gray-modern-200;
      color: $pod-gray-modern-900;
    }
  }

  &--dark {
    #{$this}__bg {
      background-color: $pod-gray-modern-900;
      color: $white;
    }
  }

  &--border {
    background: $pod-brand-gradient;
    padding: 2px;
    border-radius: 20px;

    #{$this}__bg {
      background-color: $white;
      color: $pod-gray-modern-900;
      padding: 4px;
    }
  }

  &--no-slot {
    #{$this}__icon-wrapper {
      margin: 0;
    }
  }

  &__shadow {
    position: absolute;
    inset: -10px;
    z-index: -1;
    width: 120%;
    height: 170%;
    max-width: none;
    left: -10%;
    top: -35%;
    pointer-events: none;
  }

  &__bg {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 0;
    border-radius: 20px;
    padding: 6px;
  }

  &__icon-wrapper {
    @include svg-wrapper;

    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: $white;
    color: $pod-gray-modern-900;
    padding: 5px;
    margin-right: 6px;
    margin-left: -12px;

    &--rotate {
      animation: rotate 1s infinite;
    }
  }
}

@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
